import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { MapPin, Calendar, Radio, TrendingUp, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { typesenseClient } from '@/lib/search/typesense/client';

interface DisplayStats {
  total: number;
  christmas: number;
  halloween: number;
  states: number;
  synchronized: number;
  featured: number;
  recentCount: number;
}

interface TopState {
  state: string;
  count: number;
}

const DisplaysOverview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoaded, setIsLoaded] = useState(false);
  const [stats, setStats] = useState<DisplayStats>({
    total: 0,
    christmas: 0,
    halloween: 0,
    states: 0,
    synchronized: 0,
    featured: 0,
    recentCount: 0,
  });
  const [topStates, setTopStates] = useState<TopState[]>([]);
  const [loading, setLoading] = useState(true);

  // Redirect to map page if user lands here with search/filter params
  useEffect(() => {
    const hasSearchParams = searchParams.toString().length > 0;
    if (hasSearchParams) {
      // Preserve all query params and redirect to map
      navigate(`/displays/map?${searchParams.toString()}`, { replace: true });
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    setIsLoaded(true);
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      // Fetch stats from Typesense
      const result = await typesenseClient.collections('displays_v2').documents().search({
        q: '*',
        query_by: 'display_name',
        filter_by: 'status:active',
        facet_by: 'state,holiday_type,featured',
        per_page: 0,
      });

      const facets = result.facet_counts || [];

      // Extract state counts
      const stateFacet = facets.find((f: any) => f.field_name === 'state');
      const stateData = stateFacet?.counts || [];
      const topStatesList = stateData
        .slice(0, 6)
        .map((s: any) => ({ state: s.value, count: s.count }));

      // Extract holiday type counts
      const holidayFacet = facets.find((f: any) => f.field_name === 'holiday_type');
      const holidayData = holidayFacet?.counts || [];
      const christmasCount = holidayData.find((h: any) => h.value === 'Christmas')?.count || 0;
      const halloweenCount = holidayData.find((h: any) => h.value === 'Halloween')?.count || 0;

      // Extract featured count
      const featuredFacet = facets.find((f: any) => f.field_name === 'featured');
      const featuredCount = featuredFacet?.counts?.find((f: any) => f.value === true)?.count || 0;

      // Get synchronized displays (displays with fm_station)
      const syncResult = await typesenseClient.collections('displays_v2').documents().search({
        q: '*',
        query_by: 'display_name',
        filter_by: 'status:active && fm_station:!=""',
        per_page: 0,
      });

      // Get recent displays (last 30 days)
      const thirtyDaysAgo = Math.floor((Date.now() - 30 * 24 * 60 * 60 * 1000) / 1000);
      const recentResult = await typesenseClient.collections('displays_v2').documents().search({
        q: '*',
        query_by: 'display_name',
        filter_by: `status:active && created_at:>=${thirtyDaysAgo}`,
        per_page: 0,
      });

      setStats({
        total: (result as any).found || 0,
        christmas: christmasCount,
        halloween: halloweenCount,
        states: stateData.length,
        synchronized: (syncResult as any).found || 0,
        featured: featuredCount,
        recentCount: (recentResult as any).found || 0,
      });

      setTopStates(topStatesList);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load display stats:', error);
      console.error('Error details:', error instanceof Error ? error.message : error);
      // Set loading to false even on error so UI doesn't hang
      setLoading(false);
    }
  };

  const handleExploreMap = () => {
    navigate('/displays/map');
  };

  const handleHolidayClick = (holiday: string) => {
    navigate(`/displays/map?holiday=${holiday}`);
  };

  const handleStateClick = (state: string) => {
    navigate(`/displays/map?state=${state}`);
  };

  return (
    <>
      <Helmet>
        <title>Discover Holiday Light Displays - LightsHunt</title>
        <meta name="description" content={`Explore ${stats.total.toLocaleString()} holiday light displays across ${stats.states} states. Find Christmas lights, Halloween decorations, and synchronized displays near you.`} />
        <link rel="canonical" href={`${window.location.origin}/displays`} />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <NavBar />

        <main className="grow">
          {/* Hero Section */}
          <section className="relative h-[60vh] flex items-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/90 via-primary/80 to-purple-900/90 z-10"></div>

            <div
              className={cn(
                "absolute inset-0 z-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000",
                isLoaded ? "opacity-30" : "opacity-0"
              )}
              style={{
                backgroundImage: `url('https://images.unsplash.com/photo-1576692155415-95f820a2c4c1?q=80&w=2080')`,
              }}
            />

            <div className="container z-20 px-6 md:px-10 max-w-6xl mx-auto">
              <div
                className={cn(
                  "flex flex-col items-center text-center space-y-8 transition-all duration-1000",
                  isLoaded ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
                )}
              >
                <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                  Discover Holiday Light Displays
                </h1>

                <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                  Explore {loading ? '...' : stats.total.toLocaleString()} amazing displays across {loading ? '...' : stats.states} states
                </p>

                {/* Live Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 w-full max-w-4xl mt-8">
                  <Card className="bg-white/10 backdrop-blur-md border-white/20 p-4 text-center">
                    <div className="text-3xl font-bold text-white">{loading ? '...' : stats.total.toLocaleString()}</div>
                    <div className="text-white/80 text-sm mt-1">Total Displays</div>
                  </Card>
                  <Card className="bg-white/10 backdrop-blur-md border-white/20 p-4 text-center">
                    <div className="text-3xl font-bold text-white">{loading ? '...' : stats.states}</div>
                    <div className="text-white/80 text-sm mt-1">States/Provinces</div>
                  </Card>
                  <Card className="bg-white/10 backdrop-blur-md border-white/20 p-4 text-center">
                    <div className="text-3xl font-bold text-white">{loading ? '...' : stats.synchronized.toLocaleString()}</div>
                    <div className="text-white/80 text-sm mt-1">Synchronized</div>
                  </Card>
                  <Card className="bg-white/10 backdrop-blur-md border-white/20 p-4 text-center">
                    <div className="text-3xl font-bold text-white">{loading ? '...' : stats.recentCount.toLocaleString()}</div>
                    <div className="text-white/80 text-sm mt-1">Added This Month</div>
                  </Card>
                </div>

                <Button
                  size="lg"
                  className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg font-semibold"
                  onClick={handleExploreMap}
                >
                  <MapPin size={20} className="mr-2" /> Explore Map
                </Button>
              </div>
            </div>
          </section>

          {/* Holiday Categories Section */}
          <section className="py-16 bg-background">
            <div className="container px-6 md:px-10 max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Browse by Holiday</h2>
                <p className="text-muted-foreground text-lg">Find the perfect display for any celebration</p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {/* Christmas Card */}
                <Card
                  className="group relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  onClick={() => handleHolidayClick('Christmas')}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-red-500/20 to-green-500/20 group-hover:from-red-500/30 group-hover:to-green-500/30 transition-all"></div>
                  <div className="relative p-8">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-6xl">🎄</div>
                      <Calendar className="text-primary" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Christmas Displays</h3>
                    <p className="text-muted-foreground mb-4">
                      Discover magical Christmas light displays, from traditional to spectacular synchronized shows
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-bold text-primary">
                        {loading ? '...' : stats.christmas.toLocaleString()}
                      </span>
                      <ChevronRight className="text-primary group-hover:translate-x-1 transition-transform" size={24} />
                    </div>
                  </div>
                </Card>

                {/* Halloween Card */}
                <Card
                  className="group relative overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  onClick={() => handleHolidayClick('Halloween')}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500/20 to-purple-500/20 group-hover:from-orange-500/30 group-hover:to-purple-500/30 transition-all"></div>
                  <div className="relative p-8">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-6xl">🎃</div>
                      <Calendar className="text-orange-500" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold mb-2">Halloween Displays</h3>
                    <p className="text-muted-foreground mb-4">
                      Explore spooky Halloween decorations, home haunts, and frightfully fun displays
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-3xl font-bold text-orange-500">
                        {loading ? '...' : stats.halloween.toLocaleString()}
                      </span>
                      <ChevronRight className="text-orange-500 group-hover:translate-x-1 transition-transform" size={24} />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </section>

          {/* Featured Regions Section */}
          <section className="py-16 bg-muted/30">
            <div className="container px-6 md:px-10 max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Popular Regions</h2>
                <p className="text-muted-foreground text-lg">Explore displays in top locations</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {topStates.map((location) => (
                  <Card
                    key={location.state}
                    className="p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-1 hover:border-primary/50"
                    onClick={() => handleStateClick(location.state)}
                  >
                    <div className="text-center">
                      <MapPin className="mx-auto mb-2 text-primary" size={28} />
                      <div className="font-bold text-lg">{location.state}</div>
                      <div className="text-2xl font-bold text-primary mt-2">
                        {location.count.toLocaleString()}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">displays</div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Special Features Section */}
          <section className="py-16 bg-background">
            <div className="container px-6 md:px-10 max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">Special Features</h2>
                <p className="text-muted-foreground text-lg">Discover unique and trending displays</p>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {/* Synchronized Displays */}
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <Radio className="text-primary mb-4" size={40} />
                  <h3 className="text-xl font-bold mb-2">Synchronized Displays</h3>
                  <p className="text-muted-foreground mb-4">
                    Light shows synchronized to FM radio music
                  </p>
                  <div className="text-3xl font-bold text-primary">
                    {loading ? '...' : stats.synchronized.toLocaleString()}
                  </div>
                </Card>

                {/* Featured Displays */}
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <TrendingUp className="text-primary mb-4" size={40} />
                  <h3 className="text-xl font-bold mb-2">Featured Displays</h3>
                  <p className="text-muted-foreground mb-4">
                    Hand-picked exceptional displays
                  </p>
                  <div className="text-3xl font-bold text-primary">
                    {loading ? '...' : stats.featured.toLocaleString()}
                  </div>
                </Card>

                {/* Recent Additions */}
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <Calendar className="text-primary mb-4" size={40} />
                  <h3 className="text-xl font-bold mb-2">Recently Added</h3>
                  <p className="text-muted-foreground mb-4">
                    New displays added this month
                  </p>
                  <div className="text-3xl font-bold text-primary">
                    {loading ? '...' : stats.recentCount.toLocaleString()}
                  </div>
                </Card>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary to-purple-900 text-white">
            <div className="container px-6 md:px-10 max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">
                Ready to Find Your Perfect Display?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Use our interactive map to explore displays in your area, get directions, and plan your holiday tour
              </p>
              <Button
                size="lg"
                className="bg-white text-primary hover:bg-white/90 rounded-full px-10 py-7 text-lg font-semibold"
                onClick={handleExploreMap}
              >
                <MapPin size={24} className="mr-2" /> Start Exploring
              </Button>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default DisplaysOverview;
